import { Component, ElementRef, Inject, Injector, OnDestroy, OnInit } from '@angular/core';
import { APP_BASE_HREF } from '@angular/common';
import { ActivatedRoute } from '@angular/router';

import { BehaviorSubject, combineLatest, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import * as uuid from 'uuid';

import { ActionKeys } from '../../../../../Common/Forms/enums/keys.enum';
import { ButtonTypesEnum } from '../../../../../Common/Forms/enums/button-types.enum';
import { CoreDialogService } from '../../../../../Core/Dialogs/services/dialog.service';
import { DistributionService } from '../../../../../Common/TargetingShared/services/distribution.service';
import { DistributionSummaryModel } from '../../../../../Common/TargetingShared/model/distribution-summary-model';
import { PendoService } from '../../../../../Common/Misc/services/pendo.service';
import { UserPreferencesService } from '../../../../../Account/UserPreferences/services/user-preferences.service';
import { ConfigService } from '../../../../../Core/Config/services/config.service';
import { DistributionTemplateService } from '../../../../../Common/TargetingShared/services/distribution-template.service';
import { sortArrayByString, sortByDate } from '../../../../../Common/Misc/helpers/sort/sort.helper';
import { ManageTemplateDialogComponent } from '../../../../../Common/MonitorShared/components/ManageTemplateDialog/manage-template-dialog.component';
import { TemplateCardModel } from '../../../../../Common/TargetingShared/model/template-card.model';
import { CurrentUserStateService } from '../../../../../Core/Users/services/current-user-state.service';
import { userPermissionsEnum } from '../../../../../Core/Users/enums/user-permissions.enum';
import { DialogIoModel } from '../../../../../Core/Dialogs/models/dialog-io.model';
import { FullProfileAnimationModel } from '../../../../../Core/Dialogs/models/full-profile-animation.model';
import { FullProfileDialogAnimationsEnum } from '../../../../../Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { OverlayAnimationStateEnum } from '../../../../../Core/Dialogs/enums/overlay-animation.enum';
import {
    AIQuickDistributionDialogComponent,
    AiDistributionContent,
    AiQuickDistributionOutput
} from '../AIQuickDistributionDialog/ai-quick-distribution-dialog.component';
import { DistributionTemplatesEnum } from '../../../../../Common/TargetingShared/enum/distribution-templates.enum';
import { SortOrderEnum } from '../../../../../api/shared/enums/sort-order.enum';
import { DataConnectionService } from '../../../../../Core/Users/services/data-connection.service';
import { EmailAccountConnectorPrivacyLevelEnum } from '../../../../../api/emailAccountConnector/enums/email-account-connector-privacy-level.enum';

enum NewDistributionTabs {
    defaultTemplate,
    savedTemplate,
    pastDistribution
}

@Component({
    selector: 'agpr-new-distribution',
    templateUrl: './new-distribution.component.html',
    styleUrls: ['./new-distribution.component.scss'],
    standalone: false
})
export class NewDistributionComponent implements OnInit, OnDestroy {

    readonly id = 'newDistribution';
    readonly distributionTemplatesEnum = DistributionTemplatesEnum;
    readonly newDistributionTabs =  NewDistributionTabs;
    readonly buttonTypes = ButtonTypesEnum;

    selectedTabIndex: NewDistributionTabs = NewDistributionTabs.defaultTemplate;
    distributionSummaries: DistributionSummaryModel[] = null;
    filteredDistributions: DistributionSummaryModel[] = null;
    savedTemplates: DistributionSummaryModel[] = null;
    savedDistributionFilterText: string = '';
    selectedDistribution: DistributionSummaryModel = null;
    selectedTemplate: DistributionTemplatesEnum = null;
    canCreateDistribution: boolean = false;
    assetPostFix: string = '';
    agilityTemplates: TemplateCardModel[] = [];
    openAiPermission: boolean;
    overlayContainer: Element;
    enableCoPilot: boolean = false;

    private individualRecipientsListId: number;
    private subscriptions: Subscription[] = [];

    constructor(
        public distributionService: DistributionService,
        public userPreferenceService: UserPreferencesService,
        public coreDialogService: CoreDialogService,
        @Inject(APP_BASE_HREF) public appBaseHref: string,
        private route: ActivatedRoute,
        private configService: ConfigService,
        private currentUserStateService: CurrentUserStateService,
        private distributionTemplateService: DistributionTemplateService,
        private pendoService: PendoService,
        public ref: ElementRef,
        public injector: Injector,
        private dataConnectionService: DataConnectionService
    ) {
        this.initializeAgilityTemplates();
    }

    ngOnInit() {
        this.overlayContainer = document.getElementById('main-content');

        this.subscriptions.push(
            this.route.queryParams.subscribe(params => {
                this.individualRecipientsListId = +params.withRecipientList || null;
            }),
            combineLatest([
                this.distributionService.getDistributionSummaries(),
                this.dataConnectionService.emailAccountConnectors$,
                this.currentUserStateService.getUser()
            ]).subscribe(([distributions, connectors, user ]) => {
                this.distributionSummaries = [];
                distributions?.forEach(distribution => {
                    if (distribution.sendFrom) {
                        connectors.forEach(connector => {
                            if (connector.connectorEmail === distribution.sendFrom.connectionEmail
                                && (connector.privacyLevel === EmailAccountConnectorPrivacyLevelEnum.SHARED || connector.userId === user.id)) {
                                this.distributionSummaries.push(distribution);
                            }
                        });
                    } else {
                        this.distributionSummaries.push(distribution);
                    }
                });

                if (this.distributionSummaries?.length) {
                    this.setFilteredDistributions();
                }
            }),
            this.userPreferenceService.getUserAppPreferences().pipe(filter(x => !!x)).subscribe(x => {
                this.canCreateDistribution = true;
            }),
            this.distributionTemplateService.all$.subscribe(templates => {
                this.savedTemplates = templates;
                sortArrayByString(this.savedTemplates, 'name', SortOrderEnum.ascending);
            }),
            this.currentUserStateService.getPermissions().pipe(filter(x => !!x)).subscribe(permissions => {
                if (permissions?.length) {
                    this.openAiPermission = permissions.includes(userPermissionsEnum.openAI);
                }
            })
        );
    }

    public setFilteredDistributions(): void {
        this.filteredDistributions = this.distributionSummaries.filter(
            (distributionListItem) => {
                return distributionListItem.name.toLowerCase().indexOf(this.savedDistributionFilterText.toLowerCase()) !== -1;
            }
        );

        this.filteredDistributions.sort(sortByDate);
    }

    public createDistribution(): void {
        try {
            const pendoCreateData = {
                type: 'DISTRIBUTION_QUICK_CREATE',
                data: {
                    wasPrCopilot: this.selectedDistribution ? null : this.enableCoPilot,
                    template: this.selectedDistribution ? null : this.selectedTemplate,
                    fromDistribution: this.selectedDistribution ? this.selectedDistribution.id : null
                }
            };
            this.pendoService.trackEvent(pendoCreateData);
            this.distributionService.setPendoCreateData(pendoCreateData);
        } catch (err) {
            // Tracking errors should not cause the following code to fail
            console.error(err);
        }
        if (this.selectedDistribution) {
            this.distributionService.cloneFromExistingDistribution(this.selectedDistribution.id, this.individualRecipientsListId);
        } else if (this.selectedTemplate) {
            if (this.enableCoPilot) {
                const ioModel = this.getAIHelperConfig();
                this.coreDialogService.open(AIQuickDistributionDialogComponent, ioModel, this.injector);

                return;
            }

            switch (this.selectedTemplate) {
                case DistributionTemplatesEnum.DISTRIBUTION_TEMPLATE_BLANK:
                    this.distributionService.createNewBlankDistribution(this.individualRecipientsListId);
                    break;
                case DistributionTemplatesEnum.DISTRIBUTION_TEMPLATE_PRESS_RELEASE:
                    this.distributionService.createPressReleaseDistribution(this.individualRecipientsListId);
                    break;
                case DistributionTemplatesEnum.DISTRIBUTION_TEMPLATE_MINIMALIST:
                    this.distributionService.createMinimalistDistribution(this.individualRecipientsListId);
                    break;
                case DistributionTemplatesEnum.DISTRIBUTION_TEMPLATE_MARKETING:
                    this.distributionService.createMarketingDistribution(this.individualRecipientsListId);
                    break;
            }
        }
    }

    private getAIHelperConfig(): DialogIoModel {
        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );
        const aiReferenceId = uuid.v4();
        const ioModel: DialogIoModel = {
            inputs: {
                animationTrigger: animationTrigger,
                overlayContainer: this.overlayContainer,
                title: 'Common.dialog.aiHelper.title',
                templateType: this.selectedTemplate,
                aiReferenceId: aiReferenceId
            },
            outputs: {
                dialogClose: (output: AiQuickDistributionOutput) => {
                    if (output?.aiGeneratedContent) {
                        this.createAiQuickDistribution(output.aiGeneratedContent, output.isPressRelease, aiReferenceId);
                    }
                }
            }
        };

        return ioModel;
    }

    private createAiQuickDistribution(aiGeneratedContent: AiDistributionContent, isPressRelease: boolean, aiReferenceId: string): void {
        switch (this.selectedTemplate) {
            case DistributionTemplatesEnum.DISTRIBUTION_TEMPLATE_BLANK:
                this.distributionService.createNewAiBlankDistribution(aiGeneratedContent, isPressRelease, this.individualRecipientsListId, aiReferenceId);
                break;
            case DistributionTemplatesEnum.DISTRIBUTION_TEMPLATE_PRESS_RELEASE:
                this.distributionService.createAiPressReleaseDistribution(aiGeneratedContent, isPressRelease, this.individualRecipientsListId, aiReferenceId);
                break;
            case DistributionTemplatesEnum.DISTRIBUTION_TEMPLATE_MINIMALIST:
                this.distributionService.createAiMinimalistDistribution(aiGeneratedContent, isPressRelease, this.individualRecipientsListId, aiReferenceId);
                break;
            case DistributionTemplatesEnum.DISTRIBUTION_TEMPLATE_MARKETING:
                this.distributionService.createAiMarketingDistribution(aiGeneratedContent, isPressRelease, this.individualRecipientsListId, aiReferenceId);
                break;
        }
    }

    public changeTabs(index: number): void {
        this.selectedTemplate = null;
        this.selectedDistribution = null;
        this.selectedTabIndex = index;

        if (index === NewDistributionTabs.defaultTemplate) {
            this.agilityTemplates.forEach(item => {
                if (item.selected) {
                    this.selectedTemplate = item.id as DistributionTemplatesEnum;
                }
            });
        }
    }

    public selectTemplate(template: TemplateCardModel, event?: any) {
        if (event instanceof KeyboardEvent && !(event.key === ActionKeys.ENTER || event.key === ActionKeys.SPACE)) {
            return;
        }

        this.agilityTemplates.forEach(item => {
            item.selected = false;
        });

        template.selected = true;
        this.selectedTemplate = template.id as DistributionTemplatesEnum;
    }

    public onRowSelect(distribution: DistributionSummaryModel, event?: any): void {
        if (event instanceof KeyboardEvent && !(event.key === ActionKeys.ENTER || event.key === ActionKeys.SPACE)) {
            return;
        }

        this.selectedDistribution = distribution;
    }


    openManageTemplateDialog(): void {
        const overlayContainer = document.getElementById('main-content');
        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );
        const io = {
            inputs: {
                animationTrigger: animationTrigger,
                overlayContainer: overlayContainer
            }
        };
        this.coreDialogService.open(ManageTemplateDialogComponent, io);
    }

    initializeAgilityTemplates(): void {
        let assetPostFix = '';
        if (this.configService.isNewBranding()) {
            assetPostFix = '_new_branding';
        }

        this.agilityTemplates = [
            {
                id: DistributionTemplatesEnum.DISTRIBUTION_TEMPLATE_BLANK,
                title: 'Discover.distribution.newDistribution.template.blank.header',
                icon: 'fa-thin fa-file',
                description: 'Discover.distribution.newDistribution.template.blank.description',
                selected: true
            },
            {
                id: DistributionTemplatesEnum.DISTRIBUTION_TEMPLATE_PRESS_RELEASE,
                title: 'Discover.distribution.newDistribution.template.pressRelease.header',
                description: 'Discover.distribution.newDistribution.template.pressRelease.description',
                image: 'assets/images/assets_pressRelease' + assetPostFix + '.jpg',
                imageAlt: 'Discover.distribution.newDistribution.template.pressRelease.altImageText'
            },
            {
                id: DistributionTemplatesEnum.DISTRIBUTION_TEMPLATE_MINIMALIST,
                title: 'Discover.distribution.newDistribution.template.minimalist.header',
                description: 'Discover.distribution.newDistribution.template.minimalist.description',
                image: 'assets/images/assets_minimalist.jpg',
                imageAlt: 'Discover.distribution.newDistribution.template.minimalist.altImageText'
            },
            {
                id: DistributionTemplatesEnum.DISTRIBUTION_TEMPLATE_MARKETING,
                title: 'Discover.distribution.newDistribution.template.marketing.header',
                description: 'Discover.distribution.newDistribution.template.marketing.description',
                image: 'assets/images/assets_marketing' + assetPostFix + '.jpg',
                imageAlt: 'Discover.distribution.newDistribution.template.marketing.altImageText'
            },
        ];

        this.selectedTemplate = DistributionTemplatesEnum.DISTRIBUTION_TEMPLATE_BLANK;
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
